import React, { useState } from 'react';
import * as yup from 'yup';
import { useNavigate } from 'react-router-dom'; // Ensure you have react-router-dom installed
// import { useAuthStore } from '../../store';

import Logo from '../../assets/images/logo.png';
import toast from 'react-hot-toast';
import BaseImage from '../../components/base/base-image';
import AppButton from '../../components/base/button';
import AppInput from '../../components/base/input';
import { useFormik } from 'formik';
import { FiCheck, FiEye, FiEyeOff } from 'react-icons/fi';
import { useAuthStore } from '../../store';

const ForgotPassword: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = React.useState(false);

  // Retrieve the query parameters from the URL
  const searchParams = new URLSearchParams(window.location.search);
  // Extract the 'auth' parameter
  const auth = searchParams.get('auth');

  const forgotPasswordSchema = yup.object({
    email: yup
      .string().trim()
      .email('Enter a valid email')
      .required('Email is required'),
  });

  const resetPasswordSchema = yup.object({
    password: yup
      .string()
      .required('Password is required')
      .min(6, 'Password must be at least 6 characters'),
    confirmPassword: yup
      .string()
      .required('Confirm password is required')
      .oneOf([yup.ref('password')], 'Passwords must match'),
  });

  const handleLoginRedirect = () => {
    navigate('/login');
  };

  const { forgotPassword, resetPassword } = useAuthStore();
  const formik = useFormik({
    initialValues: auth
      ? { password: '', confirmPassword: '' }
      : {
          email: '',
        },
    validationSchema: auth ? resetPasswordSchema : forgotPasswordSchema,
    validateOnMount: true,
    async onSubmit(values) {
      setLoading(true);
      const response = auth
        ? await resetPassword({
            password: values.password || '',
            confirmPassword: values.confirmPassword || '',
            token: auth,
          })
        : await forgotPassword(values.email?.trim() || '');
      if (response && response.isSuccess) {
        toast.success(
          auth
            ? 'Password reset successfully.'
            : 'Verification link sent successfully.',
        );
        handleLoginRedirect();
      }
      setLoading(false);
    },
  });

  return (
    <div className="flex items-center justify-center min-h-screen bg-white email-verified-bg">
      <div className="max-w-md p-6 bg-transparent text-center">
        <form onSubmit={formik.handleSubmit} action="#">
          <div className="flex justify-center mb-4">
            <BaseImage imgSrc={Logo} imgAlt="ryno" imgClass="mb-4 w-32" />
          </div>
          <h1 className="text-2xl font-bold text-gray-800 mb-2">
            Reset your password
          </h1>
          <p className="mb-6">
            {auth
              ? 'Enter your new password and submit to reset it.'
              : 'Enter your email address and we’ll send you password reset instructions.'}
          </p>

          {auth ? (
            <div>
              <div className="mb-5">
                <AppInput
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  id="password"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.password}
                  placeholder="Enter your password"
                  required
                  label="Password"
                  endIcon={
                    <button
                      type="button"
                      className={`absolute px-4 right-0`}
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <FiEye /> : <FiEyeOff />}
                    </button>
                  }
                  errorMessage={
                    formik.touched.password && formik.errors.password
                      ? formik.errors.password
                      : ''
                  }
                />
              </div>

              <div className="mb-5">
                <AppInput
                  type={showPassword ? 'text' : 'password'}
                  name="confirmPassword"
                  id="confirmPassword"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.confirmPassword}
                  placeholder="Confirm your password"
                  required
                  label="Confirm your Password"
                  endIcon={
                    <button
                      type="button"
                      className={`absolute px-4 right-0`}
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <FiEye /> : <FiEyeOff />}
                    </button>
                  }
                  errorMessage={
                    formik.touched.confirmPassword &&
                    formik.errors.confirmPassword
                      ? formik.errors.confirmPassword
                      : ''
                  }
                />
              </div>
            </div>
          ) : (
            <div className="mb-5">
              <AppInput
                type="text"
                name="email"
                id="email"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.email}
                placeholder="Enter your email address"
                required
                label="Registered Email"
                errorMessage={
                  formik.touched.email && formik.errors.email
                    ? formik.errors.email
                    : ''
                }
              />
            </div>
          )}

          <AppButton
            label={auth ? 'Reset Password' : 'Send Verification Link'}
            type="primary"
            extraClass="mb-4"
            submitType="submit"
            disabled={loading || !formik.isValid}
            loading={loading}
          />
          <AppButton
            label="Back to Login"
            type="light"
            clicked={handleLoginRedirect}
          />
        </form>
      </div>
    </div>
  );
};

export default ForgotPassword;
